import type { LandingPageContent } from "../types";
import { dataRoomHandoffContent } from "../dataRoomHandoff";
import { payrollContent } from "../payroll";

export const svContent: LandingPageContent = {
  locale: "sv",
  htmlLang: "sv",
  routePath: "/",
  meta: {
    title: "DynamicBooks - agentdriven bokföring, moms och rapporter",
    description:
      "Begär en icke-bindande serviceuppskattning för bokföring, momsstöd, bankmatchning, SIE-migrering, rapporter, Data Rooms och förväntad Agent Credit-kapacitet med mänsklig granskning."
  },
  brand: {
    name: "DynamicBooks",
    homeAriaLabel: "DynamicBooks startsida"
  },
  navigation: {
    skipToContentLabel: "Gå till innehåll",
    localeSwitchLabel: "English",
    localeSwitchAriaLabel: "Byt till engelska",
    mobileMenuOpenLabel: "Öppna navigering",
    mobileMenuCloseLabel: "Stäng navigering",
    mobileMenuPanelLabel: "Navigeringsmeny",
    items: [
      { kind: "link", id: "services", label: "Tjänster", href: "#services" },
      { kind: "link", id: "accounting-agent", label: "Bokföringsagent", href: "#accounting-agent" },
      { kind: "link", id: "data-rooms", label: "Data Rooms", href: "#data-rooms" },
      { kind: "link", id: "quickbooks", label: "QuickBooks", href: "/quickbooks" },
      {
        kind: "menu",
        id: "software",
        label: "Programvara",
        href: "#software-pricing",
        items: [
          { id: "reports", label: "Rapporter", href: "#reports" },
          { id: "payroll", label: "Lön", href: "#payroll" },
          { id: "bas", label: "BAS", href: "#reports" },
          { id: "ap-ar", label: "AP/AR", href: "#reports" },
          { id: "sie", label: "SIE", href: "#reports" },
          { id: "fixed-assets", label: "Anläggningar", href: "#reports" }
        ]
      },
      { kind: "link", id: "software-pricing", label: "Priser", href: "#software-pricing" },
      { kind: "cta", id: "estimate", label: "Begär uppskattning", href: "#estimate" },
      { kind: "secondary", id: "login", label: "Logga in", href: "#login-handoff" }
    ]
  },
  hero: {
    eyebrow: "BOKFÖRING, RAPPORTERING OCH AGENTSTÖD",
    title: "Redovisningstjänster för bolag som vill ha renare bokföring, momsstöd och mindre manuell administration",
    lead:
      "DynamicBooks kombinerar bokföringsprogram, säkra Data Rooms, agentassisterad förberedelse och mänsklig granskning så att transaktioner, rapporter, momsunderlag och bokföringsevidens hålls organiserade och spårbara.",
    primaryCta: { label: "Begär serviceuppskattning", href: "#estimate" },
    secondaryCta: { label: "Se programvarupriser", href: "#software-pricing" },
    trustCues: [
      "Bokföringsstöd",
      "Förberedelse av momsrapport",
      "Bankmatchning",
      "SIE-migrering",
      "AP/AR-granskning",
      "Data Rooms",
      "Rapporter och evidens"
    ]
  },
  audience: {
    id: "audience",
    eyebrow: "Målgrupp",
    title: "Vem passar DynamicBooks för?",
    lead: "Byggt för företag som vill ha mindre bokföringsadministration och mer kontroll",
    groups: [
      {
        id: "designed-for",
        title: "DynamicBooks är utformat för",
        items: [
          "Svenska små och medelstora företag",
          "Internationella grundare som driver svenska bolag",
          "Växande företag med ökande bokföringsbelastning",
          "Företag som hanterar återkommande bokföring, momsrapportering och rapporteringskrav",
          "Företag som planerar migreringar, städuppdrag eller bokföringsarbete i kapp"
        ]
      },
      {
        id: "helps",
        title: "Hur DynamicBooks hjälper",
        items: [
          "Minska manuell bokföring och ekonomiadministration",
          "Hålla transaktioner, dokument och rapporter organiserade",
          "Förbereda momsrapportering och finansiella rapporter med större trygghet",
          "Förenkla migreringar, avstämningar och löpande bokföringsarbete",
          "Behålla tydlig evidens och spårbarhet i bokföringsflöden"
        ]
      },
      {
        id: "approach",
        title: "DynamicBooks arbetssätt",
        items: [
          "Genom att kombinera bokföringsprogram, AI-agenter, säkra Data Rooms och mänsklig granskning ger DynamicBooks en strukturerad väg från uppstart till återkommande ekonomiarbete."
        ]
      }
    ]
  },
  seoInfoCards: {
    id: "seo-info",
    eyebrow: "Nyckelområden",
    title: "Allt du behöver för att hantera din bokföring.",
    lead:
      "DynamicBooks samlar de verktyg och tjänster företag behöver för att hantera löpande bokföring, moms, bankflöden, rapportering, migreringar och bokslut i ett strukturerat arbetsflöde. Utforska nyckelområdena nedan för att se hur varje del stöder din bokföringsprocess.",
    groups: [
      {
        id: "bookkeeping",
        title: "Bokföring",
        itemIds: ["bas-chart", "bank-flows", "ap-ar"]
      },
      {
        id: "compliance-reporting",
        title: "Efterlevnad och rapportering",
        itemIds: ["vat-report", "year-end", "sie-migration"]
      },
      {
        id: "collaboration-platform",
        title: "Samarbete och plattform",
        itemIds: ["data-rooms", "agent-credits"]
      }
    ],
    items: [
      {
        id: "bas-chart",
        title: "BAS-kontoplan",
        body: "Hantera din BAS-kontoplan med tydliga kontostrukturer, saldon, momskopplingar och full spårbarhet för svensk bokföring.",
        keyword: "svenskt bokföringsprogram",
        frontAssetId: "seo-bas-chart-front",
        assetId: "seo-bas-chart"
      },
      {
        id: "vat-report",
        title: "Momsrapporter",
        body: "Förbered momsrapporter med inbyggd validering, avvikelsekontroller och stödjande dokumentation före inlämning.",
        keyword: "momsrapport Sverige",
        frontAssetId: "seo-vat-report-front",
        assetId: "seo-vat-report"
      },
      {
        id: "bank-flows",
        title: "Bankmatchning",
        body: "Importera banktransaktioner, matcha betalningar och identifiera omatchade eller saknade underlag mer effektivt.",
        keyword: "bankintegration bokföring",
        frontAssetId: "seo-bank-flows-front",
        assetId: "seo-bank-flows"
      },
      {
        id: "sie-migration",
        title: "SIE-migrering",
        body: "Importera befintlig bokföringsdata med det svenska SIE-formatet och bevara historiska poster och bokföringssammanhang.",
        keyword: "SIE-migrering",
        frontAssetId: "seo-sie-migration-front",
        assetId: "seo-sie-migration"
      },
      {
        id: "ap-ar",
        title: "AP/AR-reskontra",
        body: "Följ kundfakturor, leverantörsfakturor, betalningsstatus och utestående saldon från en plats.",
        keyword: "kundreskontra leverantörsreskontra",
        frontAssetId: "seo-ap-ar-front",
        assetId: "seo-ap-ar"
      },
      {
        id: "year-end",
        title: "Bokslutsstöd",
        body: "Förbered bokslutsrapporter, stängningsaktiviteter och stödjande dokumentation med strukturerade granskningsflöden.",
        keyword: "bokslutsstöd",
        frontAssetId: "seo-year-end-front",
        assetId: "seo-year-end"
      },
      {
        id: "data-rooms",
        title: "Data Rooms",
        body: "Lagra bokföringsunderlag, stödjande dokument, rapporter och kommunikation i säkra bolagsarbetsytor med full spårbarhet.",
        keyword: "data room bokföring",
        frontAssetId: "seo-data-rooms-front",
        assetId: "seo-data-rooms"
      },
      {
        id: "agent-credits",
        title: "Bokföringsagent",
        body: "Agent credits representerar den körkapacitet som AI-bokföringsagenter använder för att förbereda bokföring, momsrapporter, avstämningar, migreringar och andra bokföringsflöden. Din uppskattning inkluderar förväntad kapacitet och månatlig Agent Credit-användning baserat på bolagets bokföringsaktivitet.",
        keyword: "Agent Credits",
        frontAssetId: "seo-agent-credits-front",
        assetId: "seo-agent-credits"
      }
    ]
  },
  quotePanel: {
    eyebrow: "Serviceuppskattning",
    title: "Få en preliminär serviceuppskattning.",
    summary:
      "Beskriv bolaget, månadsvolymen, momsperioden, bankkällor, rapportbehov och eventuell migrering eller städning. DynamicBooks ger en icke-bindande servicevy innan slutlig omfattning bekräftas.",
    expandLabel: "Starta uppskattningsförfrågan",
    collapseLabel: "Stäng uppskattningsförfrågan",
    previewTitle: "",
    resultBackLabel: "Tillbaka till uppskattningsformuläret",
    fields: [
      { label: "Bolagsuppgifter", value: "Namn, e-post, organisationsnummer eller momsregistreringsnummer" },
      { label: "Månatlig bokföringsvolym", value: "Transaktioner, fakturor, kvitton och lönevolym" },
      { label: "Omfattning och migrering", value: "SIE, städning, bokslut och anläggningar" }
    ],
    lanes: ["Förfrågan", "Offert", "Uppstart"],
    disclaimer:
      "Alla uppskattningar är preliminära och icke-bindande tills underlag, källor, omfattning och ansvarsnivå har bekräftats."
  },
  services: {
    id: "services",
    eyebrow: "Tjänster",
    title: "Bokföringstjänster byggda runt din arbetsbelastning.",
    lead:
      "Varje bolag har olika bokföringskrav. I stället för att erbjuda en fast prislista uppskattar DynamicBooks lämplig tjänst utifrån transaktionsvolym, bokföringskomplexitet, rapporteringskrav, datakällor och nivån av agent- och mänskligt stöd som behövs.",
    leadSecondary:
      "Varje bokföringstjänst kombinerar AI-bokföringsagenter, strukturerade arbetsflöden och mänsklig granskning. Agentassisterad förberedelse hjälper till att minska manuellt arbete samtidigt som bokföringsbeslut hålls transparenta, spårbara och under kontroll.",
    items: [
      {
        title: "Löpande bokföring",
        body: "Löpande bokföring som håller banktransaktioner, kvitton, leverantörsfakturor, kundfakturor och momsperioder organiserade genom strukturerade agentassisterade arbetsflöden med mänsklig granskning där det behövs.",
        tags: ["Transaktioner", "Kvitton", "Momsperioder"],
        assetId: "delivery-software-first"
      },
      {
        title: "Bankmatchning",
        body: "Importera banktransaktioner från källor som stöds, förbered matchningsförslag automatiskt, identifiera avvikelser och granska bokföringar innan de blir en del av bokföringsposterna.",
        tags: ["Bankimporter", "Betalningsmatchning", "Avvikelsegranskning"],
        assetId: "delivery-hybrid"
      },
      {
        title: "Momsstöd",
        body: "Förbered momsrapporter med automatiserad validering, avvikelsekontroller, stödjande underlag och rapportartefakter före inlämning eller granskning.",
        tags: ["Momsvalidering", "Rapportartefakter", "Inlämningsförberedelse"],
        assetId: "delivery-review-evidence"
      },
      {
        title: "Migrering",
        body: "Migrera historisk bokföringsdata, städa upp tidigare bokföring, hantera anläggningstillgångar, förbered årsredovisningar och slutför bokslutsaktiviteter genom strukturerade migrerings- och granskningsflöden.",
        tags: ["SIE-migrering", "Historisk städning", "Bokslutsstöd"],
        assetId: "delivery-human-assisted"
      }
    ]
  },
  accountingAgent: {
    id: "accounting-agent",
    eyebrow: "Bokföringsagent",
    title: "AI-bokföringsagenter förbereder arbetet. Människor behåller kontrollen.",
    lead:
      "DynamicBooks använder AI-bokföringsagenter för att förbereda bokföring, momsrapportering, avstämningar, migreringar och rapportunderlag i kontrollerade arbetsflöden. Mänsklig granskning, omfattningskontroll och bekräftelse är fortsatt en del av leveransen innan bokföringsarbetet går vidare.",
    relatedLink: {
      label: "Läs artikeln om agentdriven bokföring i praktiken",
      href: "/artiklar/agentdriven-bokforing"
    },
    illustration: {
      ariaLabel:
        "DynamicBooks-arbetsflöde där företagets bokföringsmaterial går in i ett säkert Data Room, där AI-bokföringsagenter och mänskliga granskare arbetar tillsammans innan bokföring, momsrapporter, finansiella rapporter och spårbar evidens tas fram.",
      input: {
        kicker: "BOLAG",
        title: "Bokföringsmaterial",
        body: "Transaktioner, dokument, bankdata, momsperioder och rapportbehov"
      },
      dataRoom: {
        kicker: "SÄKER ARBETSYTA",
        title: "DynamicBooks Data Room",
        status: "Spårbart",
        items: ["Bokföringsposter", "Bankkällor", "Momsunderlag", "Rapporter", "Stödjande evidens"]
      },
      collaboration: {
        agent: {
          kicker: "FÖRBEREDELSE",
          title: "AI-bokföringsagenter",
          responsibilities: [
            "Läser strukturerat bokföringsmaterial",
            "Förbereder bokföringsförslag",
            "Kontrollerar moms- och rapportunderlag",
            "Identifierar avvikelser och saknad evidens",
            "Skapar torrkörningar och rapportutkast"
          ],
          credits: {
            label: "Agent Credits",
            title: "Mätbar körkapacitet",
            body: "Uppskattningar inkluderar den förväntade kapacitet som krävs för bolagets bokföringsflöden."
          }
        },
        linkLabel: "Kontrollerat arbetsflöde",
        human: {
          kicker: "KONTROLL",
          title: "Mänsklig granskning och omfattningskontroll",
          responsibilities: [
            "Granskar undantag och avvikelser",
            "Granskar beslut som påverkar ansvarsnivån",
            "Bekräftar omfattning och leveransantaganden",
            "Hanterar komplexa fall och fall med högre risk",
            "Godkänner nästa bokföringssteg"
          ],
          review: {
            status: "Klar för bekräftelse",
            body: "SIE-migrering, städning, lön, bokslutsarbete och andra komplexa fall följer ytterligare kontroller där det krävs."
          }
        }
      },
      confirmation: {
        kicker: "BEKRÄFTAT ARBETSFLÖDE",
        title: "Omfattning, ansvar, antaganden och nästa steg är bekräftade"
      },
      outputs: [
        {
          title: "Bokföring",
          body: "Förberedda transaktioner, matchningsförslag och granskningshistorik"
        },
        {
          title: "Moms",
          body: "Validerat momsunderlag, avvikelsekontroller och rapportartefakter"
        },
        {
          title: "Rapporter",
          body: "Finansiella rapporter, rapportpaket och stödjande material"
        },
        {
          title: "Evidens",
          body: "Spårbara beslut, förhandsvisningar, filer, kvittenser och bekräftelser"
        }
      ],
      processLabel: "Bokföringsagentens process"
    },
    steps: [
      {
        title: "Förbered",
        body: "Samla bolagsinformation, bokföringsvolym, bankkällor, momsperioder, rapportkrav och migrationsdetaljer.",
        assetId: "agent-prepare"
      },
      {
        title: "Torrkör",
        body: "Generera förslag, identifiera avvikelser och förbered moms- och rapportunderlag utan att ändra bokföringsposter.",
        assetId: "agent-dry-run"
      },
      {
        title: "Kontrollera",
        body: "Styr migreringar, städning, lön, bokslutsarbete och komplexa fall genom rätt omfattnings- och granskningskontroller.",
        assetId: "agent-review"
      },
      {
        title: "Bekräfta",
        body: "Bekräfta omfattning, ansvar, leveranssätt, Agent Credit-kapacitet och nästa steg innan bokföringsarbetet börjar.",
        assetId: "agent-confirm"
      }
    ]
  },
  dataRooms: {
    id: "data-rooms",
    eyebrow: dataRoomHandoffContent.eyebrow.sv,
    title: dataRoomHandoffContent.title.sv,
    lead: dataRoomHandoffContent.lead.sv,
    boundary: dataRoomHandoffContent.boundary.sv,
    steps: dataRoomHandoffContent.stages.map((stage) => ({
      title: stage.title.sv,
      body: stage.body.sv,
      assetId: stage.assetId
    }))
  },
  reports: {
    id: "reports",
    eyebrow: "Rapporter",
    title: "Rapporter och spårbara bokföringsunderlag",
    lead:
      "DynamicBooks hjälper bolag att se vad siffrorna bygger på, vilka frågor som återstår och vilka beslut som har tagits.",
    modules: [
      {
        key: "bas_account_plan",
        title: "BAS-kontoplan",
        body: "Kontokategorier, SIE-kontotyper, momskodsmappning, giltighet och aktiv/inaktiv kontroll.",
        detailLabel: "Kontoplan",
        assetId: "module-chart-balances"
      },
      {
        key: "account_balances",
        title: "Kontosaldon",
        body: "Saldon per valt datum för kontonummer, kontonamn, kategori och balans.",
        detailLabel: "Saldon",
        assetId: "module-chart-balances"
      },
      {
        key: "general_ledger",
        title: "Huvudbok",
        body: "Konto- och datumdrilldown med debet, kredit, verifikationsetiketter och beskrivningar.",
        detailLabel: "Huvudbok",
        assetId: "module-ledger-journals"
      },
      {
        key: "journal_list",
        title: "Verifikationslista",
        body: "Sökbara verifikationer med relationskontext, bokföringsstatus och export.",
        detailLabel: "Verifikationer",
        assetId: "module-ledger-journals"
      },
      {
        key: "balance_sheet",
        title: "Balansrapport",
        body: "Räkenskapsår, kontorader, ingående och utgående saldo, förändring samt HTML/PDF-utdata.",
        detailLabel: "Balans",
        assetId: "module-vat-reports"
      },
      {
        key: "profit_loss",
        title: "Resultatrapport",
        body: "Period- och räkenskapsårsvyer med intäkter, kostnader, resultat, kontorader och export.",
        detailLabel: "Resultat",
        assetId: "module-vat-reports"
      },
      {
        key: "vat_reports_eskd_pdf_xml",
        title: "Momsrapport, PDF och XML/eSKD",
        body: "Förbered och validera momsrapporter, generera artefakter och registrera inlämningsevidens utan inlämningsgarantier.",
        detailLabel: "Moms",
        assetId: "module-vat-reports"
      },
      {
        key: "bank_transactions_revolut_sources",
        title: "Banktransaktioner och Revolut-källor",
        body: "Granska, importera, synka där konfigurerat, föreslå och bokför banktransaktioner; Revolut-källor är lokalt evidensade.",
        detailLabel: "Bank",
        assetId: "module-bank-records"
      },
      {
        key: "ap_ar_ledgers",
        title: "AP/AR-reskontra",
        body: "Öppna och historiska reskontror, verifikationslänkning, betalstatus, kandidatsökning och markera-som-betald-flöde.",
        detailLabel: "Reskontra",
        assetId: "module-ap-ar"
      },
      {
        key: "sie_import_export_dedupe",
        title: "SIE import/export och dubblettkontroll",
        body: "Import och export med kontroll, seriemappning, dubblettkontroll, korrigeringskedjor och betalningsmatchning.",
        detailLabel: "SIE",
        assetId: "module-sie-migration"
      },
      {
        key: "fixed_assets_depreciation",
        title: "Anläggningar och avskrivning",
        body: "Anläggningsregister, avskrivning, nedskrivning, journallinjeroller, avstämning och export.",
        detailLabel: "Anläggningar",
        assetId: "module-fixed-assets"
      },
      {
        key: "reports_register_annual_artifacts",
        title: "Rapportregister och årsartefakter",
        body: "Katalog, förhandsvisningar, genererade artefakter, historik, inlämningsstatus och års/SKV-liknande stöd där flödet är konfigurerat.",
        detailLabel: "Rapporter",
        assetId: "module-report-register"
      }
    ]
  },
  payroll: payrollContent.sv,
  quickBooks: {
    id: "quickbooks",
    eyebrow: "QuickBooks Online",
    title: "Anslutning till QuickBooks Online",
    lead: "Granska behörigheter för enbart bokföring och anslutningens tillgänglighet. Payments ingår inte i det nuvarande anslutningsflödet.",
    action: { href: "/quickbooks", label: "Granska QuickBooks-anslutningen" }
  },
  estimate: {
    id: "estimate",
    eyebrow: "Uppskattning",
    title: "Preliminär serviceuppskattning utan offentlig fast prislista",
    lead:
      "Uppskattningen kombinerar plattformsåtkomst, bokföringsarbete, förväntade Agent Credits och omfattningssignaler utan att publik text blir slutlig fakturering.",
    packages: [
      {
        title: "Start",
        body: "Lägre volym, löpande bokföring och fokuserade behov för moms och rapporter.",
        assetId: "pricing-start"
      },
      {
        title: "Tillväxt",
        body: "Fler transaktioner, flera källor, AP/AR, rapportkadens eller måttlig omfattning.",
        assetId: "pricing-growth"
      },
      {
        title: "Operatör",
        body: "Operativa bokföringsflöden, Data Rooms, avancerade rapporter och högre uppstartsintensitet.",
        assetId: "pricing-operator"
      },
      {
        title: "Migrering och städning",
        body: "SIE-migrering, ikappföring, städning, anläggningar eller boksluts- och rapportpaket.",
        assetId: "pricing-cleanup"
      }
    ],
    notes: [
      "Slutligt pris, omfattning, ansvarsnivå och startdatum bekräftas efter käll- och omfattningskontroll.",
      "Agent Credits beskriver förväntad körkapacitet, inte slutlig fakturering.",
      "Komplexa eller omfattande ärenden går via guidad uppstart."
    ]
  },
  faq: {
    id: "faq",
    eyebrow: "FAQ",
    title: "Vanliga frågor före uppskattningen",
    items: [
      {
        question: "Vad är DynamicBooks?",
        answer:
          "DynamicBooks är en bokföringstjänst och ett agentdrivet arbetsflöde för företag som vill få ordning på transaktioner, moms, rapporter och redovisningsunderlag."
      },
      {
        question: "Är uppskattningen bindande?",
        answer:
          "Nej. Uppskattningen är preliminär tills underlag, källor, ansvarsnivå och omfattning har bekräftats."
      },
      {
        question: "Kan DynamicBooks hantera momsrapport?",
        answer:
          "DynamicBooks kan förbereda och validera momsrapport, momsunderlag och artefakter som PDF och XML/eSKD där arbetsflödet är konfigurerat."
      },
      {
        question: "Vilka bankkällor kan användas?",
        answer:
          "Bankfiler, manuella importer och Revolut-källor kan ingå där de är konfigurerade. Ingen universell banktäckning utlovas."
      },
      {
        question: "Kan jag importera SIE?",
        answer:
          "SIE-import, export och dubblettkontroll kan ingå i migrering eller historisk städning och hanteras under uppstartens omfattningskontroll."
      },
      {
        question: "Hur fungerar fakturering och abonnemang?",
        answer:
          "DynamicBooks visar bara icke-bindande rekommendationer. Fakturering, betalning och konto- eller abonnemangshantering sker i DynamicMail efter inloggning."
      },
      {
        question: "Vad är Agent Credits?",
        answer:
          "Agent Credits beskriver förväntad körkapacitet för agentarbete i Data Rooms. De är en planeringssignal, inte ett slutligt faktureringsbeslut."
      },
      {
        question: "När behövs guidad uppstart?",
        answer:
          "Guidad uppstart används vid SIE, städning, bokslut, lönevolym, okända källor, riskmarkörer eller låg konfidens i uppskattningen."
      },
      {
        question: "Vilken lönehantering stödjer DynamicBooks?",
        answer:
          "DynamicBooks stödjer löneimport, beräkningar, batchkontroller, utlägg, journalförhandsvisning, export av utbetalningsutkast, skattetabellsunderlag och rapportartefakter. Inlämningsansvar och löneservice fastställs separat."
      },
      {
        question: "Hur används Data Rooms?",
        answer:
          "Data Rooms samlar underlag, torrkörningar, omfattningssignaler, rapportartefakter och överlämning till DynamicMail för fortsatt leverans."
      }
    ]
  },
  footer: {
    id: "footer",
    owner: "DynamicBooks från Eregis",
    summary:
      "Bokföringsprogram, rapporter, Data Rooms och agentstöd för svenska bolag.",
    copyright: "© 2026 Eregis, Inc.",
    groups: [
      {
        id: "product",
        title: "Produkt",
        links: [
          { id: "software", label: "Programvara", href: "#software-pricing" },
          { id: "services", label: "Tjänster", href: "#services" },
          { id: "pricing", label: "Priser", href: "#software-pricing" },
          { id: "reports", label: "Rapporter", href: "#reports" }
        ]
      },
      {
        id: "workflows",
        title: "Arbetsflöden",
        links: [
          { id: "agentdriven-article", label: "Agentdriven bokföring", href: "/artiklar/agentdriven-bokforing" },
          { id: "accounting-agent", label: "Bokföringsagent", href: "#accounting-agent" },
          { id: "data-rooms", label: "Data Rooms", href: "#data-rooms" },
          { id: "bas-vat", label: "BAS och moms", href: "#seo-info" },
          { id: "sie-migration", label: "SIE och migrering", href: "#reports" }
        ]
      },
      {
        id: "trust",
        title: "Förtroende",
        links: [
          { id: "privacy", label: "Integritet", href: "https://eregis.com/privacy" },
          { id: "terms", label: "Villkor", href: "https://eregis.com/terms" },
          { id: "quickbooks-terms", label: "QuickBooks-villkor", href: "/quickbooks/terms" },
          { id: "quickbooks-privacy", label: "QuickBooks integritetsinformation", href: "/quickbooks/privacy" },
          { id: "cookies", label: "Cookieinställningar", href: "#footer", action: "cookie-settings" },
          { id: "contact", label: "Kontakt", href: "#estimate" }
        ]
      },
      {
        id: "access",
        title: "Åtkomst",
        links: [
          { id: "login", label: "Logga in", href: "#login-handoff" },
          { id: "estimate", label: "Begär serviceuppskattning", href: "#estimate" }
        ]
      }
    ],
    cmp: {
      bannerTitle: "Cookies och integritet",
      bannerBody:
        "Vi använder nödvändiga cookies för att webbplatsen ska fungera. Om analys används ska du kunna välja det separat.",
      necessaryOnlyLabel: "Endast nödvändiga",
      acceptAllLabel: "Acceptera alla",
      settingsLabel: "Inställningar",
      settingsTitle: "Cookieinställningar",
      necessaryCategory: "Nödvändiga: krävs för grundläggande funktion och säkerhet.",
      analyticsCategory: "Analys: hjälper oss förstå hur webbplatsen används om du samtycker.",
      analyticsToggleLabel: "Tillåt analyscookies",
      saveChoiceLabel: "Spara val",
      closeLabel: "Stäng",
      currentChoiceLabel: "Aktuellt val",
      necessarySaved: "Endast nödvändiga cookies är sparade.",
      analyticsSaved: "Analyscookies är godkända."
    }
  }
};
